import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { /* Injector, */ inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { ImpersonationService, VIEW_AS_HEADER } from '../services/impersonation.service';
// ERROR LOG DISABLED (2026-10-28) — the Admin API error endpoints aren't built
// yet, so failed calls are not reported for this release. To re-enable, restore
// ErrorLogService / Injector here and the three lines marked below, plus the
// 'errors' route (admin.routes.ts) and its sidebar link (admin-shell.component.ts).
import { ERROR_LOG_URL /*, ErrorLogService */ } from '../services/error-log.service';
import { environment } from '../../../environments/environment';
import { readAuthToken } from '../auth-token.storage';

let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const toast = inject(ToastService);

  // The PDF render service sends its token in the request body — skip injecting
  // the Authorization header so the CORS preflight only needs content-type.
  if (req.url.startsWith(environment.disclosurePdfApiUrl)) {
    return next(req);
  }

  // "View as": tell the Admin API who the staff member is acting as, so the
  // activity log records both. Admin API only — the other APIs' hand-written
  // CORS allow-lists would reject an unknown header on the preflight. Injected
  // here rather than at the top: DevUserService (behind ImpersonationService)
  // makes Admin API calls of its own, and this keeps construction acyclic.
  if (req.url.startsWith(environment.adminApiUrl)) {
    const viewed = inject(ImpersonationService).viewedUser();
    if (viewed) req = req.clone({ setHeaders: { [VIEW_AS_HEADER]: String(viewed.id) } });
  }

  const token = auth.token() ?? readAuthToken();
  // ERROR LOG DISABLED — see note at the imports.
  // const startedAt = Date.now();
  // const injector = inject(Injector);
  return next(addToken(req, token)).pipe(
    catchError(error => {
      const isAuthEndpoint = req.url.includes('/auth/login') || req.url.includes('/auth/token/refresh') || req.url.includes('/auth/mfa/');
      if (error.status === 401 && !isAuthEndpoint) {
        return handle401(req, next, auth);
      }
      if (error.status === 429) {
        toast.show('Too many requests — please wait a moment and try again.', 'warn');
      }
      // ERROR LOG DISABLED — see note at the imports.
      // if (shouldReportError(req, error)) {
      //   // Fetched on failure only — keeps the error log out of every request's
      //   // construction path (it depends on services that make calls of their own).
      //   injector.get(ErrorLogService).reportHttpError(req, error, startedAt);
      // }
      return throwError(() => error);
    })
  );
};

/**
 * Which failures go to the error log (decision D8). Everything the server or
 * network refused, except:
 *  - 401s on API calls — handled above by the token refresh, not failures;
 *  - 4xx from the Auth API — a wrong password or expired code is the user's
 *    mistake, not an error (5xx and network failures there are still kept);
 *  - the error log's and activity tracker's own endpoints, so a broken log
 *    can't feed itself.
 * The PDF render service never gets this far (it returns early above).
 */
export function shouldReportError(req: HttpRequest<unknown>, error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  if (req.url.startsWith(ERROR_LOG_URL) || req.url.includes('/api/activity/client-events')) return false;
  const status = error.status;
  if (status === 401) return false;
  if (req.url.startsWith(environment.authApiUrl) && status >= 400 && status < 500) return false;
  return status === 0 || status >= 400;
}

function addToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;
}

/** True when the app is being driven headlessly for a PDF render. */
function isPrerender(): boolean {
  return typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('prerender') === '1';
}

function handle401(req: HttpRequest<unknown>, next: HttpHandlerFn, auth: AuthService) {
  if (!auth.refreshToken()) {
    // Only redirect to /login when the user had an active session that expired.
    // Use hadSession() (not isAuthenticated()) because a page refresh past the
    // token TTL clears the expired token before this runs — isAuthenticated()
    // would then be false and the user would be stranded on a blank page
    // instead of being sent to log in again. If there was never a session
    // (e.g. Playwright prerender), let the error propagate silently so public
    // routes like /disclosures/report don't get bounced to the login page.
    // The PDF render service injects the caller's auth token but no refresh
    // token, which makes hadSession() true inside a prerender. Logging out would
    // navigate the headless page to /login and abort the export, so a prerender
    // stays put and renders with whatever data it managed to load.
    if (auth.hadSession() && !isPrerender()) {
      auth.logout();
    }
    return throwError(() => new Error('Session expired. Please log in again.'));
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshSubject.next(null);
    const attempted = auth.refreshToken();

    return auth.refreshAccessToken().pipe(
      switchMap(res => {
        isRefreshing = false;
        refreshSubject.next(res.authToken);
        return next(addToken(req, res.authToken));
      }),
      catchError(err => {
        isRefreshing = false;

        // Lost a rotation race. Tabs share one refresh token, and /token/refresh
        // issues a new one each time, so two tabs renewing at the same moment
        // means the slower one spends a token the other already replaced. The
        // winner broadcasts the new pair, so if our token has since changed,
        // this failure is that race and not a dead session — retry with the new
        // one rather than signing every tab out.
        const current = auth.refreshToken();
        if (current && current !== attempted) {
          refreshSubject.next(auth.token());
          return next(addToken(req, auth.token()));
        }

        auth.logout();
        return throwError(() => err);
      })
    );
  }

  // Another request is already refreshing — wait for the new token, then retry.
  return refreshSubject.pipe(
    filter(token => token !== null),
    take(1),
    switchMap(token => next(addToken(req, token)))
  );
}
